/**
 * F43-FC (§6.5) — endorsement (catalog-badge) moderation dialog.
 *
 * One dialog for the two `ModerateEndorsementInput` actions (remove | restore).
 * A SegmentedControl picks the action; a mandatory reason Textarea is required
 * for both. Posts via the parent's `onConfirm`, which calls
 * `adminRecommendationApi.moderateEndorsement(id, { action, reason })` →
 * `POST /admin/endorsements/:id/moderate` (perm `endorsements:moderate`).
 *
 * Composes F02 primitives only (Modal / SegmentedControl / FormField /
 * Textarea / Button / Banner). Mirrors `ModerateReviewDialog`.
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, SegmentedControl, Textarea } from '@bravemove/ui-web';
import type { ModerateEndorsementInput } from '../../api/recommendation';

export interface ModerateEndorsementDialogProps {
  readonly open: boolean;
  /** The catalog badge being moderated (id + a label for the dialog header). */
  readonly badge: { readonly id: string; readonly label: string; readonly revoked: boolean } | null;
  readonly onConfirm: (input: ModerateEndorsementInput) => Promise<void>;
  readonly onClose: () => void;
  readonly errorMessage?: string | null;
}

const ENDORSEMENT_ACTIONS = ['remove', 'restore'] as const;

export function ModerateEndorsementDialog({
  open,
  badge,
  onConfirm,
  onClose,
  errorMessage,
}: ModerateEndorsementDialogProps) {
  const { t } = useI18n();

  const [action, setAction] = useState<'remove' | 'restore'>('remove');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    // Default to the opposite of the current state (revoked badge → restore).
    setAction(badge?.revoked ? 'restore' : 'remove');
    setReason('');
  }, [open, badge]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const valid = useMemo(() => reason.trim().length >= 1 && reason.length <= 500, [reason]);

  const actionOptions = useMemo(
    () =>
      ENDORSEMENT_ACTIONS.map((a) => ({
        value: a,
        label: t(`admin:f43.endorsement.action.${a}`, {
          defaultValue: a === 'remove' ? 'Revoke' : 'Restore',
        }),
      })),
    [t],
  );

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await onConfirm({ action, reason });
    } catch {
      // PU-14 — the parent (`confirmBadge`) has already surfaced the error
      // (banner/toast) and re-throws; swallow here so `void submit()` never
      // escapes as an unhandled rejection. The dialog stays open.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={t('admin:f43.endorsement.dialog_title', { defaultValue: 'Moderate badge' })}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="endorsement-moderate-cancel"
          >
            {t('admin:recommendation.catalog.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant={action === 'restore' ? 'primary' : 'destructive'}
            onClick={() => void submit()}
            loading={submitting}
            disabled={!valid || submitting}
            data-testid="endorsement-moderate-confirm"
          >
            {t('admin:recommendation.moderation.confirm', { defaultValue: 'Apply' })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="moderate-endorsement-dialog">
        {errorMessage ? <Banner tone="danger">{errorMessage}</Banner> : null}

        <Banner tone="warning">
          {t('admin:recommendation.moderation.audit_notice', {
            defaultValue: 'Audit-logged action — the reason is recorded against your admin id.',
          })}
        </Banner>

        {badge ? (
          <p className="text-[13px] text-ink-3" data-testid="endorsement-moderate-label">
            {badge.label}
          </p>
        ) : null}

        <FormField
          label={t('admin:recommendation.moderation.action_label', { defaultValue: 'Action' })}
          required
        >
          <SegmentedControl
            value={action}
            onChange={(v) => setAction(v as 'remove' | 'restore')}
            options={actionOptions}
            aria-label={t('admin:recommendation.moderation.action_label', {
              defaultValue: 'Action',
            })}
          />
        </FormField>

        <FormField
          label={t('recommendation:moderation.reason_label', { defaultValue: 'Reason (required)' })}
          required
        >
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder={t('admin:recommendation.moderation.reason_placeholder', {
              defaultValue: 'Why are you taking this action?',
            })}
            data-testid="endorsement-moderate-reason"
          />
        </FormField>
      </div>
    </Modal>
  );
}
